import type { CostRecipe } from "./cost-recipe";
import { directDerivation, type QuantityDerivation } from "./quantity-dimensions";
import { matchesDimensionLabel } from "./work-dimensions";

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

/** Only library-declared references may supply missing inputs. They remain visible
 * assumptions, not drawing evidence, and never change the line's quantity formula. */
export function ensureRecipeReferenceDimensions(data: QuantityDerivation | null | undefined,
  recipe?: CostRecipe | null): QuantityDerivation | undefined {
  if (!recipe) return data ?? undefined;
  const models = [
    recipe.labour_pricing, recipe.plant_pricing,
    ...(recipe.shape === "specification" ? [recipe.supply_pricing] : []),
    ...recipe.materials.flatMap(m => [m.quantity_pricing, m.dimension_pricing]),
  ].filter(m => m != null);
  const refs = models.flatMap(m => (m.reference_dimensions ?? []).map(d => ({ ...d, evidence: m.evidence })));
  if (!refs.length) return data ?? undefined;
  const result = { ...(data ?? directDerivation()), dimensions: [...(data?.dimensions ?? [])] };
  // Earlier snapshots may contain a default axis beside real named instances.
  // Remove only untouched, generated defaults, never a builder-entered value.
  result.dimensions = result.dimensions.filter(d => !(d.id.startsWith("recipe-reference-") && d.source === "assumed"
    && result.dimensions.some(other => other.id !== d.id && !other.id.startsWith("recipe-reference-")
      && matchesDimensionLabel(other.label, d.label))));
  for (const ref of refs) {
    const key = norm(ref.label);
    // Ambiguous or wrong-unit measurements require review, not a second guessed axis.
    if (result.dimensions.some(d => matchesDimensionLabel(d.label, ref.label))) continue;
    const peers = refs.filter(d => norm(d.label) === key);
    if (peers.some(d => d.value !== ref.value || d.unit !== ref.unit)) continue;
    if (result.dimensions.length >= 60) break;
    const prefix = `recipe-reference-${key.replace(/ /g, "-")}`.slice(0, 90);
    let id = prefix, suffix = 1;
    while (result.dimensions.some(d => d.id === id)) id = `${prefix}-${suffix++}`;
    result.dimensions.push({ id, label: ref.label, value: ref.value, unit: ref.unit,
      source: "assumed", note: `Cost Library reference size, not measured from drawings. Confirm or edit for this item. ${ref.evidence}`.slice(0, 1000) });
  }
  return result;
}
